import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Button, Space, message, DatePicker, List, Empty, Alert, Typography, Switch, Popconfirm, Modal, Descriptions, Divider, QRCode, Row, Col, Select, Input, InputNumber } from 'antd';
import { EyeOutlined, CheckCircleOutlined, ThunderboltOutlined, EditOutlined, CheckSquareOutlined, PrinterOutlined, QrcodeOutlined, MessageOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
// Динамический импорт qrcode для совместимости

import { ordersAPI, API_BASE_URL, orderIssuesAPI, orderCorrectionsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  formatOrderDeliveryAddress,
  formatEmployeeName,
  getCustomerResponsibleContact,
  getCancelNegotiationListLabel,
  CANCEL_MIN_REASON_LEN,
  CANCEL_MAX_REASON_LEN
} from '../utils/orderDisplay';
import { roleLabel } from '../utils/labels';

const { Text } = Typography;

const CORRECTION_TYPE_OPTIONS = [
  { value: 'wrong_item', label: 'Другое блюдо / перепутали' },
  { value: 'missing', label: 'Не довезли позицию' },
  { value: 'quantity', label: 'Неверное количество' },
  { value: 'quality', label: 'Качество / состояние' },
  { value: 'other', label: 'Другое' }
];

const CORRECTION_STATUS_LABEL: Record<string, string> = {
  pending_customer: 'Ожидает подтверждения заказчика',
  approved: 'Согласовано',
  rejected: 'Отклонено'
};

const Orders: React.FC = () => {
  dayjs.locale('ru');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<any[]>([]);
  const { user } = useAuthStore();
  const isCustomer = user?.role === 'customer' || user?.role === 'customer_manager';
  const isCustomerOnly = user?.role === 'customer';
  const isCustomerManager = user?.role === 'customer_manager';
  const canApproveCorrection = isCustomerOnly || isCustomerManager;
  const isSupplier = user?.role === 'supplier';
  const canProposeCorrection = isSupplier || isCustomerManager;
  const isDelivery = user?.role === 'delivery';
  const isEmployee = user?.role === 'employee';
  const [poolDate, setPoolDate] = React.useState<Dayjs>(dayjs());
  const [poolData, setPoolData] = React.useState<any | null>(null);
  const [poolLoading, setPoolLoading] = React.useState(false);
  const [weeklyPools, setWeeklyPools] = React.useState<Record<string, any>>({});
  const [confirmingPool, setConfirmingPool] = React.useState(false);
  const [confirmingPoolDates, setConfirmingPoolDates] = React.useState<Set<string>>(new Set());
  const [orderDetailModal, setOrderDetailModal] = React.useState<{ visible: boolean; order: any | null }>({
    visible: false,
    order: null
  });
  const [checklistModal, setChecklistModal] = React.useState<{ visible: boolean; order: any | null }>({
    visible: false,
    order: null
  });
  const [cancelAgreeForOrderId, setCancelAgreeForOrderId] = React.useState<number | null>(null);
  const [cancelReasonDraft, setCancelReasonDraft] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [issueModal, setIssueModal] = React.useState<{
    visible: boolean;
    orderId: number;
    subjectEmployeeId?: number | null;
    subjectMenuItemId?: number | null;
    subjectLabel?: string;
    message: string;
    loading: boolean;
  }>({
    visible: false,
    orderId: 0,
    message: '',
    loading: false
  });
  const [correctionForm, setCorrectionForm] = React.useState({
    reductionAmount: 0,
    reason: '',
    discrepancyType: 'missing',
    subjectEmployeeId: undefined as number | undefined,
    subjectMenuItemId: undefined as number | undefined
  });
  const [correctionSubmitting, setCorrectionSubmitting] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if ((user?.role === 'customer' || user?.role === 'customer_manager') && user.customerId) {
        params.customerId = user.customerId;
      }
      if (user?.role === 'supplier' && user.supplierId) {
        params.supplierId = user.supplierId;
      }
      const res = await ordersAPI.getOrders(params);
      setData(res);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось загрузить заказы');
    } finally {
      setLoading(false);
    }
  };

  const loadPool = async (date: Dayjs) => {
    if (!isCustomer) return;
    setPoolLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/pool/${date.format('YYYY-MM-DD')}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setPoolData(result);
      } else {
        const error = await response.json().catch(() => ({}));
        if (error?.error) {
          message.warning(error.error);
        }
        setPoolData(null);
      }
    } catch (e) {
      console.error(e);
      setPoolData(null);
      message.error('Не удалось загрузить черновой заказ');
    } finally {
      setPoolLoading(false);
    }
  };

  const loadWeeklyPools = async () => {
    if (!isCustomer) return;
    const result: Record<string, any> = {};
    const token = localStorage.getItem('token');
    const start = dayjs().startOf('day');
    const days = Array.from({ length: 7 }).map((_, i) => start.add(i, 'day'));
    await Promise.all(
      days.map(async (d) => {
        try {
          const resp = await fetch(`${API_BASE_URL}/api/daily-menu/pool/${d.format('YYYY-MM-DD')}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (resp.ok) {
            const json = await resp.json();
            result[d.format('YYYY-MM-DD')] = json;
          } else {
            result[d.format('YYYY-MM-DD')] = null;
          }
        } catch {
          result[d.format('YYYY-MM-DD')] = null;
        }
      })
    );
    setWeeklyPools(result);
  };

  React.useEffect(() => { 
    load(); 
  }, [user]);

  React.useEffect(() => {
    if (isCustomer) {
      loadPool(poolDate);
      loadWeeklyPools();
    }
  }, [isCustomer, poolDate]);

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: 'orange',
      confirmed: 'blue',
      in_preparation: 'cyan',
      in_delivery: 'purple',
      delivered: 'green',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: { [key: string]: string } = {
      pending: 'Ожидает',
      confirmed: 'Подтвержден',
      in_preparation: 'Готовится',
      in_delivery: 'В доставке',
      delivered: 'Доставлен',
      cancelled: 'Отменен'
    };
    return texts[status] || status;
  };

  const capitalize = (text: string) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const getOrderMenuDayRaw = (order: any) =>
    order?.deliveryDate || order?.orderPool?.deliveryDate || order?.employeeOrderRecords?.[0]?.deliveryDate || null;

  const renderOrderDeliveryDayCell = (record: any) => {
    const raw = getOrderMenuDayRaw(record);
    if (!raw) {
      return <Tag color="error">Не указан</Tag>;
    }
    const d = dayjs(raw);
    return (
      <Space direction="vertical" size={0}>
        <Text strong style={{ color: '#1677ff' }}>{capitalize(d.format('dddd'))}</Text>
        <Text>{d.format('DD.MM.YYYY')}</Text>
      </Space>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70
    },
    {
      title: 'День доставки',
      key: 'deliveryDay',
      width: 150,
      render: (_: any, record: any) => renderOrderDeliveryDayCell(record)
    },
    {
      title: 'Оформлен',
      dataIndex: 'createdAt',
      width: 118,
      render: (date: string) =>
        new Date(date).toLocaleString('ru', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
    },
    {
      title: 'Сумма',
      dataIndex: 'totalAmount',
      render: (amount: number) => `${amount} ₽`
    },
    {
      title: 'Статус',
      key: 'orderStatus',
      render: (_: unknown, record: any) => {
        const neg = getCancelNegotiationListLabel(record);
        return (
          <Space direction="vertical" size={4}>
            <Tag color={getStatusColor(record.status)}>{getStatusText(record.status)}</Tag>
            {neg ? (
              <Tag color="volcano" style={{ margin: 0 }}>
                {neg}
              </Tag>
            ) : null}
          </Space>
        );
      }
    },
    {
      title: 'Проверен',
      dataIndex: 'checklistVerified',
      render: (verified: boolean, record: any) => {
        if (isSupplier) {
          return (
            <Popconfirm
              title={verified ? 'Отменить проверку заказа?' : 'Отметить заказ как проверенный?'}
              onConfirm={() => handleToggleChecklistVerified(record.id, !verified)}
              okText="Да"
              cancelText="Нет"
            >
              <Switch
                checked={verified}
                checkedChildren="Да"
                unCheckedChildren="Нет"
                size="small"
              />
            </Popconfirm>
          );
        }
        return (
          <Tag color={verified ? 'green' : 'default'}>
            {verified ? <><CheckCircleOutlined /> Да</> : 'Нет'}
          </Tag>
        );
      }
    },
    {
      title: 'Действия',
      render: (_: any, record: any) => (
        <Space>
          <Button 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => loadOrderDetails(record.id)}
          >
            Просмотр
          </Button>
          {isSupplier && (
            <Button 
              icon={<CheckSquareOutlined />} 
              size="small"
              type={record.checklistVerified ? 'default' : 'primary'}
              onClick={() => handleOpenChecklist(record.id)}
            >
              Чек-лист
            </Button>
          )}
          {(isDelivery || isSupplier) && record.status === 'in_delivery' && (
            <Popconfirm
              title="Отметить заказ доставленным?"
              okText="Да"
              cancelText="Нет"
              onConfirm={() => handleMarkDelivered(record.id)}
            >
              <Button size="small" type="primary">
                Доставлен
              </Button>
            </Popconfirm>
          )}
          {isDelivery && record.status === 'in_delivery' && !record.receiptConfirmedAt && (
            <Tag color="warning">Ждём подтверждения заказчика</Tag>
          )}
        </Space>
      )
    }
  ];

  const renderPoolCard = () => {
    if (!isCustomer) return null;

    const meetsMinOrders = poolData?.minOrders
      ? poolData.itemsCount >= poolData.minOrders
      : true;
    const meetsMinAmount = poolData?.minOrderAmount
      ? poolData.totalAmount >= poolData.minOrderAmount
      : true;
    const canSend =
      !!poolData?.id &&
      poolData.itemsCount > 0 &&
      (meetsMinOrders || meetsMinAmount);

    return (
      <Card
        title="Черновой заказ"
        style={{ marginBottom: 24 }}
        loading={poolLoading}
        extra={
          <DatePicker
            value={poolDate}
            format="DD.MM.YYYY"
            onChange={(value) => value && setPoolDate(value.startOf('day'))}
          />
        }
      >
        {poolData?.emergencyActive && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Активирован внеплановый заказ"
            description={
              poolData.emergencyExpiresAt
                ? `Действует до ${dayjs(poolData.emergencyExpiresAt).format('DD.MM HH:mm')}`
                : 'Действует до отключения'
            }
          />
        )}
        {poolData?.itemsCount ? (
          <>
            <Space wrap style={{ marginBottom: 16 }}>
              <Tag color="blue">Позиции: {poolData.itemsCount}</Tag>
              <Tag color="purple">Сумма: {poolData.totalAmount} ₽</Tag>
              {poolData.minOrders ? (
                <Tag color={meetsMinOrders ? 'green' : 'orange'}>
                  Минимум {poolData.minOrders} шт.
                </Tag>
              ) : null}
              {poolData.minOrderAmount ? (
                <Tag color={meetsMinAmount ? 'green' : 'orange'}>
                  Сумма {poolData.minOrderAmount} ₽
                </Tag>
              ) : null}
            </Space>
            <List
              dataSource={poolData.items}
              renderItem={(item: any) => (
                <List.Item>
                  <List.Item.Meta
                    title={`${item.menuItem?.name || 'Блюдо'} — ${item.quantity} шт`}
                    description={`Сотрудник: ${
                      item.user?.fullName || item.user?.username || 'Сотрудник'
                    }`}
                  />
                  <Text>
                    {(item.menuItem?.price || 0) * item.quantity} ₽
                  </Text>
                </List.Item>
              )}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 16
              }}
            >
              <Text strong>Итого: {poolData.totalAmount} ₽</Text>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={confirmingPool}
                disabled={!canSend}
                onClick={handleConfirmPool}
              >
                Отправить заказ
              </Button>
            </div>
            {!canSend && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Заказ можно отправить при выполнении одного из условий: количества или суммы.
              </Text>
            )}
          </>
        ) : (
          <Empty description="Нет позиций в заказе на выбранную дату" />
        )}
      </Card>
    );
  };

  const renderWeeklyPools = () => {
    if (!isCustomer) return null;
    const days = Object.keys(weeklyPools).sort();
    const blocks = days
      .map((key) => ({ key, pool: weeklyPools[key] }))
      .filter(({ pool }) => pool && pool.items && pool.items.length > 0);
    if (blocks.length === 0) return null;

    return (
      <Card title="Черновые заказы по дням (7 дней)">
        <Space direction="vertical" style={{ width: '100%' }}>
          {blocks.map(({ key, pool }) => {
            const dateLabel = dayjs(key).format('DD.MM.YYYY');
            const meetsMinOrders = pool?.minOrders ? pool.itemsCount >= pool.minOrders : true;
            const meetsMinAmount = pool?.minOrderAmount ? pool.totalAmount >= pool.minOrderAmount : true;
            const canSend = pool.itemsCount > 0 && (meetsMinOrders || meetsMinAmount);
            const isConfirming = confirmingPoolDates.has(key);
            
            return (
              <Card 
                key={key} 
                size="small" 
                title={dateLabel} 
                extra={
                  <Space>
                    <Tag>{pool.itemsCount} поз.</Tag>
                    <Button
                      type="primary"
                      size="small"
                      icon={<CheckCircleOutlined />}
                      loading={isConfirming}
                      disabled={!canSend}
                      onClick={() => handleConfirmPoolByDate(key)}
                    >
                      Отправить
                    </Button>
                  </Space>
                }
              >
                <Space wrap style={{ marginBottom: 8 }}>
                  <Tag color="purple">Сумма: {pool.totalAmount} ₽</Tag>
                  {pool.minOrders ? (
                    <Tag color={meetsMinOrders ? 'green' : 'orange'}>Мин. шт: {pool.minOrders}</Tag>
                  ) : null}
                  {pool.minOrderAmount ? (
                    <Tag color={meetsMinAmount ? 'green' : 'orange'}>Мин. сумма: {pool.minOrderAmount} ₽</Tag>
                  ) : null}
                </Space>
                {!canSend && (
                  <Alert
                    type="warning"
                    message="Заказ не соответствует минимальным требованиям"
                    style={{ marginBottom: 8 }}
                  />
                )}
                <List
                  dataSource={pool.items}
                  renderItem={(item: any) => {
                    const userId = item.user?.id;
                    const isSubmitted = pool.submittedUserIds?.includes?.(userId);
                    const submissionInfo = pool.submissionDetails?.[userId];
                    const allowEdit = submissionInfo?.allowEdit || false;
                    
                    return (
                      <List.Item
                        actions={
                          isSubmitted
                            ? [
                                <Space key="edit-control" direction="vertical" size="small">
                                  <Tag color="green" icon={<CheckCircleOutlined />}>
                                    Подтверждено
                                  </Tag>
                                  <Popconfirm
                                    title={`${allowEdit ? 'Запретить' : 'Разрешить'} редактирование?`}
                                    onConfirm={() => handleAllowEdit(pool.id, userId, !allowEdit)}
                                    okText="Да"
                                    cancelText="Нет"
                                  >
                                    <Switch
                                      size="small"
                                      checked={allowEdit}
                                      checkedChildren={<EditOutlined />}
                                      unCheckedChildren={<EditOutlined />}
                                      title={allowEdit ? 'Редактирование разрешено' : 'Редактирование запрещено'}
                                    />
                                  </Popconfirm>
                                </Space>
                              ]
                            : undefined
                        }
                      >
                        <List.Item.Meta
                          title={`${item.menuItem?.name || 'Блюдо'} — ${item.quantity} шт`}
                          description={
                            <>
                              <div>
                                Сотрудник: {item.user?.fullName || item.user?.username || 'Сотрудник'}
                              </div>
                              {item.employeeNote ? (
                                <div style={{ marginTop: 4 }}>
                                  <Text type="secondary">Комментарий: {item.employeeNote}</Text>
                                </div>
                              ) : null}
                            </>
                          }
                        />
                        <Text>{(item.menuItem?.price || 0) * item.quantity} ₽</Text>
                      </List.Item>
                    );
                  }}
                />
              </Card>
            );
          })}
        </Space>
      </Card>
    );
  };

  const handleConfirmPool = async () => {
    if (!poolData || !poolData.itemsCount) {
      message.warning('Нет позиций для отправки');
      return;
    }
    setConfirmingPool(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: poolDate.format('YYYY-MM-DD')
        })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const excluded: string[] = data?.excludedEmployees ?? [];
        if (excluded.length > 0) {
          message.warning(
            `Заказ отправлен. Исключены из отправки (лимит): ${excluded.join('; ')}`
          );
        } else {
          message.success('Заказ отправлен поставщику');
        }
        loadPool(poolDate);
        load();
        loadWeeklyPools();
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось отправить заказ');
      }
    } catch (e) {
      message.error('Ошибка соединения с сервером');
    } finally {
      setConfirmingPool(false);
    }
  };

  const loadOrderDetails = async (orderId: number) => {
    try {
      const order = await ordersAPI.getOrder(orderId);
      setOrderDetailModal({ visible: true, order });
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось загрузить детали заказа');
    }
  };

  const applyOrderToModals = (order: any) => {
    setOrderDetailModal((prev) =>
      prev.order?.id === order.id ? { visible: prev.visible, order } : prev
    );
    setChecklistModal((prev) =>
      prev.order?.id === order.id ? { visible: prev.visible, order } : prev
    );
  };

  const handleMarkDelivered = async (orderId: number) => {
    try {
      const updated = await ordersAPI.updateOrderStatus(orderId, 'delivered');
      message.success('Заказ отмечен доставленным');
      applyOrderToModals(updated);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось обновить статус');
    }
  };

  const handleConfirmReceipt = async (orderId: number) => {
    try {
      const updated = await ordersAPI.confirmOrderReceipt(orderId);
      message.success('Получение заказа подтверждено');
      applyOrderToModals(updated);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось подтвердить получение');
    }
  };

  const handleCancelAgree = async (orderId: number, reason: string) => {
    const updated = await ordersAPI.agreeOrderCancel(orderId, reason);
    if (updated.status === 'cancelled') {
      message.success('Заказ отменён по согласию сторон');
    } else {
      message.info('Ваше согласие на отмену учтено. Ожидаем вторую сторону.');
    }
    applyOrderToModals(updated);
    load();
  };

  const openCancelAgreeModal = (orderId: number) => {
    setCancelReasonDraft('');
    setCancelAgreeForOrderId(orderId);
  };

  const submitCancelAgreeModal = async () => {
    if (cancelAgreeForOrderId == null) return;
    const t = cancelReasonDraft.trim();
    if (t.length < CANCEL_MIN_REASON_LEN) {
      message.warning(`Укажите причину отмены (не менее ${CANCEL_MIN_REASON_LEN} символов).`);
      return Promise.reject(new Error('reason-too-short'));
    }
    try {
      await handleCancelAgree(cancelAgreeForOrderId, t);
      setCancelAgreeForOrderId(null);
      setCancelReasonDraft('');
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось сохранить согласие');
      return Promise.reject(e);
    }
  };

  const supplierNextStatusOptions = (current: string) => {
    if (current === 'pending') return [{ value: 'confirmed', label: 'Подтверждён' }];
    if (current === 'confirmed') return [{ value: 'in_preparation', label: 'Готовится' }];
    if (current === 'in_preparation') return [{ value: 'in_delivery', label: 'В доставке' }];
    return [];
  };

  const handleSupplierSetStatus = async (orderId: number, status: string) => {
    try {
      const updated = await ordersAPI.updateOrderStatus(orderId, status);
      message.success('Статус обновлён');
      applyOrderToModals(updated);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось сменить статус');
    }
  };

  const handleOpenChecklist = async (orderId: number) => {
    try {
      const order = await ordersAPI.getOrder(orderId);
      setChecklistModal({ visible: true, order });
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось загрузить заказ для чек-листа');
    }
  };

  const renderMenuDayTitleBox = (order: any) => {
    const raw = getOrderMenuDayRaw(order);
    const d = raw ? dayjs(raw) : null;
    const ok = !!(d && d.isValid());
    return (
      <div
        style={{
          minWidth: 200,
          maxWidth: 320,
          padding: '8px 14px',
          background: 'linear-gradient(180deg, #e6f7ff 0%, #f5fcff 100%)',
          border: '1px solid #91d5ff',
          borderRadius: 8
        }}
      >
        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>День доставки</div>
        <div style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', marginTop: 2 }}>
          Дата выбора блюд сотрудниками
        </div>
        {ok ? (
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1677ff', marginTop: 6, lineHeight: 1.35 }}>
            {capitalize(d!.format('dddd, DD.MM.YYYY'))}
          </div>
        ) : (
          <Tag color="error" style={{ marginTop: 8 }}>
            Не указан
          </Tag>
        )}
      </div>
    );
  };

  const formatTitle = (text?: string | null) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const printQRCode = async (poolItem: any, order: any) => {
    const printDate = new Date().toLocaleDateString('ru', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Получаем название компании заказчика из разных возможных источников
    const companyName = order.customer?.user?.companyName 
      || order.customer?.companyName 
      || order.customer?.name 
      || (order.customerId ? `Заказчик #${order.customerId}` : '');
    const supplierName = order.supplier?.user?.companyName 
      || order.supplier?.companyName 
      || 'Поставщик';
    const supplierLine = supplierName;
    const orderLine = `Заказ №${order.id}`;
    const rawDeliveryDate = getOrderMenuDayRaw(order);
    const deliveryDateObj = rawDeliveryDate ? dayjs(rawDeliveryDate) : null;
    const deliveryDateText = deliveryDateObj ? capitalize(deliveryDateObj.format('dddd DD.MM.YYYY')) : '';
    const dishName = formatTitle(poolItem.menuItem?.name || 'Блюдо');
    const dishLine = dishName;
    const dateLine = deliveryDateText ? `На ${deliveryDateText}` : '';
    const employeeName = formatEmployeeName(poolItem.user);
    const customerLine = companyName ? `${employeeName} (${companyName})` : employeeName;
    
    const qrData = JSON.stringify({
      orderId: order.id,
      poolItemId: poolItem.id,
      menuItemId: poolItem.menuItemId,
      menuItemName: poolItem.menuItem?.name,
      quantity: poolItem.quantity,
      userId: poolItem.userId,
      username: employeeName,
      fullName: employeeName,
      customerId: order.customerId,
      supplierId: order.supplierId,
      deliveryDate: order.deliveryDate,
      printDate: printDate,
      supplierName,
      supplierLine,
      dishLine,
      dateLine,
      deliveryDateText,
      customerLine,
      packagedAt: printDate
    });

    try {
      // Динамически импортируем и генерируем QR-код в основном окне
      const QRCodeLib = await import('qrcode');
      const toDataURL = QRCodeLib.default?.toDataURL || QRCodeLib.toDataURL;
      const qrCodeDataURL = await toDataURL(qrData, {
        width: 250,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Открываем новое окно для печати с уже готовым QR-кодом
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>QR-код: ${poolItem.menuItem?.name || 'Блюдо'}</title>
              <style>
                @media print {
                  body { margin: 0; padding: 0; }
                  @page {
                    size: 58mm 40mm;
                    margin: 0;
                  }
                }
                body { 
                  display: flex; 
                  flex-direction: row; 
                  align-items: center; 
                  justify-content: center; 
                  margin: 0;
                  padding: 1.5mm;
                  font-family: Arial, sans-serif;
                  width: 58mm;
                  height: 40mm;
                  box-sizing: border-box;
                }
                .label { 
                  text-align: left; 
                  padding: 1.5mm;
                  width: 100%;
                  height: 100%;
                  box-sizing: border-box;
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  justify-content: space-around;
                  gap: 2mm;
                }
                .qr-section {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                }
                .info-section {
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  justify-content: center;
                  flex: 1;
                  min-width: 0;
                }
                h3 { margin: 0; font-size: 8px; font-weight: bold; line-height: 1.1; color: #000; }
                .info, .print-date { margin: 0.2mm 0; font-size: 8px; line-height: 1.1; color: #000; font-weight: 400; }
                .qr-container {
                  display: flex;
                  justify-content: center;
                  margin: 0;
                }
                .qr-image {
                  display: block;
                  width: 22mm;
                  height: 22mm;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                .username {
                  font-weight: bold;
                  color: #1890ff;
                  font-size: 8px;
                }
                .print-date {
                  margin-top: 0.3mm;
                }
              </style>
            </head>
            <body>
              <div class="label">
                <div class="qr-section">
                  <div class="qr-container">
                    <img src="${qrCodeDataURL}" alt="QR Code" class="qr-image" />
                  </div>
                </div>
                <div class="info-section">
                  <div class="info supplier-line">${supplierLine}</div>
                  <div class="info order-line">${orderLine}</div>
                  <h3>${dishLine}</h3>
                  ${dateLine ? `<div class="info date-line">${dateLine}</div>` : ''}
                  <div class="info">для <span class="username">${customerLine}</span></div>
                  <div class="info">Кол-во: ${poolItem.quantity} шт</div>
                  <div class="print-date">Упаковка: ${printDate}</div>
                </div>
              </div>
              <script>
                // Ждем загрузки изображения, затем печатаем
                window.onload = function() {
                  var img = document.querySelector('.qr-image');
                  if (img) {
                    img.onload = function() {
                      setTimeout(function() {
                        window.print();
                      }, 300);
                    };
                    // Если изображение уже загружено
                    if (img.complete) {
                      setTimeout(function() {
                        window.print();
                      }, 300);
                    }
                  } else {
                    setTimeout(function() {
                      window.print();
                    }, 500);
                  }
                };
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      message.error('Ошибка генерации QR-кода');
    }
  };

  const handleToggleChecklistVerified = async (orderId: number, verified: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/checklist`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ verified })
      });

      if (response.ok) {
        message.success(verified ? 'Заказ отмечен как проверенный' : 'Проверка заказа отменена');
        load(); // Обновляем список заказов
        // Обновляем заказ в модальном окне, если оно открыто
        if (checklistModal.order && checklistModal.order.id === orderId) {
          setChecklistModal(prev => ({
            ...prev,
            order: { ...prev.order, checklistVerified: verified }
          }));
        }
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось изменить статус проверки');
      }
    } catch (e) {
      message.error('Ошибка соединения с сервером');
    }
  };

  const handleAllowEdit = async (poolId: number, userId: number, allow: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/allow-edit/${poolId}/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ allow })
      });

      if (response.ok) {
        message.success(allow ? 'Редактирование разрешено' : 'Редактирование запрещено');
        loadWeeklyPools();
        if (poolDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')) {
          loadPool(poolDate);
        }
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось обновить разрешение');
      }
    } catch (e) {
      message.error('Ошибка соединения с сервером');
    }
  };

  const handleConfirmPoolByDate = async (date: string) => {
    const dateKey = dayjs(date).format('YYYY-MM-DD');
    const pool = weeklyPools[dateKey];
    
    if (!pool || !pool.itemsCount) {
      message.warning('Нет позиций для отправки на эту дату');
      return;
    }

    setConfirmingPoolDates(prev => new Set(prev).add(dateKey));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: dateKey
        })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const excluded: string[] = data?.excludedEmployees ?? [];
        if (excluded.length > 0) {
          message.warning(
            `Заказ на ${dayjs(dateKey).format('DD.MM.YYYY')} отправлен. Исключены (лимит): ${excluded.join('; ')}`
          );
        } else {
          message.success(`Заказ на ${dayjs(dateKey).format('DD.MM.YYYY')} отправлен поставщику`);
        }
        loadWeeklyPools();
        load();
        if (poolDate.format('YYYY-MM-DD') === dateKey) {
          loadPool(poolDate);
        }
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось отправить заказ');
      }
    } catch (e) {
      message.error('Ошибка соединения с сервером');
    } finally {
      setConfirmingPoolDates(prev => {
        const next = new Set(prev);
        next.delete(dateKey);
        return next;
      });
    }
  };

  const filteredOrders = React.useMemo(() => {
    if (statusFilter === 'all') return data;
    return data.filter((o) => o.status === statusFilter);
  }, [data, statusFilter]);

  const openIssueDiscussion = (
    orderId: number,
    opts?: { subjectEmployeeId?: number; subjectMenuItemId?: number; subjectLabel?: string }
  ) => {
    setIssueModal({
      visible: true,
      orderId,
      subjectEmployeeId: opts?.subjectEmployeeId ?? null,
      subjectMenuItemId: opts?.subjectMenuItemId ?? null,
      subjectLabel: opts?.subjectLabel,
      message: '',
      loading: false
    });
  };

  const submitIssueDiscussion = async () => {
    if (!issueModal.message.trim() || issueModal.message.trim().length < 3) {
      message.warning('Введите сообщение (не менее 3 символов)');
      return;
    }
    setIssueModal((prev) => ({ ...prev, loading: true }));
    try {
      const result = await orderIssuesAPI.openThread({
        orderId: issueModal.orderId,
        subjectEmployeeId: issueModal.subjectEmployeeId ?? undefined,
        subjectMenuItemId: issueModal.subjectMenuItemId ?? undefined,
        message: issueModal.message.trim()
      });
      message.success(result.created ? 'Обращение создано' : 'Открыто существующее обращение');
      setIssueModal((prev) => ({ ...prev, visible: false, loading: false }));
      setOrderDetailModal({ visible: false, order: null });
      navigate(`/order-issues?threadId=${result.thread.id}`);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось открыть обращение');
      setIssueModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const refreshOrderInModal = async (orderId: number) => {
    const order = await ordersAPI.getOrder(orderId);
    setOrderDetailModal((prev) => (prev.order?.id === orderId ? { visible: prev.visible, order } : prev));
    return order;
  };

  const handleProposeCorrection = async () => {
    const order = orderDetailModal.order;
    if (!order) return;
    const reduction = Number(correctionForm.reductionAmount);
    if (!reduction || reduction <= 0) {
      message.warning('Укажите сумму уменьшения больше 0');
      return;
    }
    if (reduction > order.totalAmount) {
      message.warning('Сумма корректировки не может превышать текущую сумму заказа');
      return;
    }
    if ((correctionForm.reason || '').trim().length < 3) {
      message.warning('Укажите причину (не менее 3 символов)');
      return;
    }
    setCorrectionSubmitting(true);
    try {
      await orderCorrectionsAPI.propose({
        orderId: order.id,
        correctionAmount: -reduction,
        reason: correctionForm.reason.trim(),
        discrepancyType: correctionForm.discrepancyType,
        subjectEmployeeId: correctionForm.subjectEmployeeId,
        subjectMenuItemId: correctionForm.subjectMenuItemId
      });
      message.success('Корректировка отправлена на подтверждение');
      setCorrectionForm({
        reductionAmount: 0,
        reason: '',
        discrepancyType: 'missing',
        subjectEmployeeId: undefined,
        subjectMenuItemId: undefined
      });
      await refreshOrderInModal(order.id);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось предложить корректировку');
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  const handleApproveCorrection = async (correctionId: number) => {
    try {
      await orderCorrectionsAPI.approve(correctionId);
      message.success('Корректировка подтверждена, сумма заказа обновлена');
      if (orderDetailModal.order) {
        await refreshOrderInModal(orderDetailModal.order.id);
      }
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось подтвердить');
    }
  };

  const handleRejectCorrection = async (correctionId: number) => {
    try {
      await orderCorrectionsAPI.reject(correctionId, 'Корректировка отклонена');
      message.success('Корректировка отклонена');
      if (orderDetailModal.order) {
        await refreshOrderInModal(orderDetailModal.order.id);
      }
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось отклонить');
    }
  };

  const renderOrderCorrectionsBlock = (order: any) => {
    const corrections: any[] = order.orderCorrections || [];
    const originalAmount = order.originalTotalAmount ?? order.totalAmount;
    const hasApproved = corrections.some((c) => c.status === 'approved');
    const pending = corrections.find((c) => c.status === 'pending_customer');
    const poolItems = order.orderPool?.items || [];

    return (
      <>
        <Divider>Корректировка суммы заказа</Divider>
        <Descriptions bordered size="small" column={1} style={{ marginBottom: 12 }}>
          <Descriptions.Item label="Исходная сумма заказа">
            <Text strong>{originalAmount} ₽</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Текущая сумма">
            <Text strong style={{ color: hasApproved ? '#52c41a' : undefined }}>
              {order.totalAmount} ₽
            </Text>
            {hasApproved && originalAmount !== order.totalAmount ? (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                (изменена после корректировки)
              </Text>
            ) : null}
          </Descriptions.Item>
        </Descriptions>

        {corrections.length > 0 ? (
          <List
            size="small"
            style={{ marginBottom: 12 }}
            dataSource={corrections}
            renderItem={(c: any) => {
              const typeLabel =
                CORRECTION_TYPE_OPTIONS.find((o) => o.value === c.discrepancyType)?.label ||
                c.discrepancyType ||
                '—';
              const subjectParts: string[] = [];
              if (c.subjectEmployee?.fullName || c.subjectEmployee?.username) {
                subjectParts.push(c.subjectEmployee.fullName || c.subjectEmployee.username);
              }
              if (c.subjectMenuItem?.name) subjectParts.push(c.subjectMenuItem.name);
              return (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space wrap>
                        <Tag
                          color={
                            c.status === 'approved'
                              ? 'success'
                              : c.status === 'rejected'
                                ? 'default'
                                : 'processing'
                          }
                        >
                          {CORRECTION_STATUS_LABEL[c.status] || c.status}
                        </Tag>
                        <Text>
                          {c.originalTotalAmount} ₽ → {c.correctedTotalAmount} ₽ (
                          {c.correctionAmount} ₽)
                        </Text>
                      </Space>
                    }
                    description={
                      <div>
                        <div>
                          <Text type="secondary">Тип: </Text>
                          {typeLabel}
                        </div>
                        {subjectParts.length > 0 && (
                          <div>
                            <Text type="secondary">По: </Text>
                            {subjectParts.join(' · ')}
                          </div>
                        )}
                        <div>
                          <Text type="secondary">Причина: </Text>
                          {c.reason}
                        </div>
                        <div>
                          <Text type="secondary">Инициатор: </Text>
                          {c.proposedBy?.fullName || c.proposedBy?.username} (
                          {roleLabel(c.proposedBy?.role)})
                        </div>
                        {c.customerApprovedBy && (
                          <div>
                            <Text type="secondary">Подтвердил: </Text>
                            {c.customerApprovedBy.fullName || c.customerApprovedBy.username}
                          </div>
                        )}
                      </div>
                    }
                  />
                  {canApproveCorrection && c.status === 'pending_customer' && (
                    <Space>
                      <Button type="primary" size="small" onClick={() => handleApproveCorrection(c.id)}>
                        Подтвердить
                      </Button>
                      <Button size="small" onClick={() => handleRejectCorrection(c.id)}>
                        Отклонить
                      </Button>
                    </Space>
                  )}
                </List.Item>
              );
            }}
          />
        ) : (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Корректировок пока нет.
          </Text>
        )}

        {pending && canApproveCorrection && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Ожидается ваше подтверждение корректировки"
            description={`Предложено уменьшить сумму на ${Math.abs(pending.correctionAmount)} ₽ (итого ${pending.correctedTotalAmount} ₽).`}
          />
        )}

        {canProposeCorrection &&
          !pending &&
          ['delivered', 'in_delivery'].includes(order.status) &&
          order.status !== 'cancelled' && (
            <Card size="small" title="Предложить корректировку" style={{ marginBottom: 8 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text type="secondary">Уменьшить сумму на (₽)</Text>
                  <InputNumber
                    min={0.01}
                    max={order.totalAmount}
                    step={0.01}
                    style={{ width: '100%', marginTop: 4 }}
                    value={correctionForm.reductionAmount || undefined}
                    onChange={(v) =>
                      setCorrectionForm((f) => ({ ...f, reductionAmount: Number(v) || 0 }))
                    }
                  />
                </div>
                <div>
                  <Text type="secondary">Тип несоответствия</Text>
                  <Select
                    style={{ width: '100%', marginTop: 4 }}
                    value={correctionForm.discrepancyType}
                    options={CORRECTION_TYPE_OPTIONS}
                    onChange={(v) => setCorrectionForm((f) => ({ ...f, discrepancyType: v }))}
                  />
                </div>
                {poolItems.length > 0 && (
                  <div>
                    <Text type="secondary">Сотрудник (необязательно)</Text>
                    <Select
                      allowClear
                      placeholder="Кому относится"
                      style={{ width: '100%', marginTop: 4 }}
                      value={correctionForm.subjectEmployeeId}
                      options={Array.from(
                        new Map(
                          poolItems.map((it: any) => [
                            it.userId,
                            {
                              value: it.userId,
                              label: it.user?.fullName || it.user?.username || `ID ${it.userId}`
                            }
                          ])
                        ).values()
                      )}
                      onChange={(v) => setCorrectionForm((f) => ({ ...f, subjectEmployeeId: v }))}
                    />
                  </div>
                )}
                {poolItems.length > 0 && (
                  <div>
                    <Text type="secondary">Блюдо (необязательно)</Text>
                    <Select
                      allowClear
                      placeholder="Какое блюдо"
                      style={{ width: '100%', marginTop: 4 }}
                      value={correctionForm.subjectMenuItemId}
                      options={Array.from(
                        new Map(
                          poolItems.map((it: any) => [
                            it.menuItemId,
                            { value: it.menuItemId, label: it.menuItem?.name || `ID ${it.menuItemId}` }
                          ])
                        ).values()
                      )}
                      onChange={(v) => setCorrectionForm((f) => ({ ...f, subjectMenuItemId: v }))}
                    />
                  </div>
                )}
                <div>
                  <Text type="secondary">Причина</Text>
                  <Input.TextArea
                    rows={3}
                    style={{ marginTop: 4 }}
                    value={correctionForm.reason}
                    onChange={(e) => setCorrectionForm((f) => ({ ...f, reason: e.target.value }))}
                    placeholder="Опишите, что не доставлено или не соответствует заказу"
                  />
                </div>
                <Button
                  type="primary"
                  loading={correctionSubmitting}
                  onClick={handleProposeCorrection}
                >
                  Отправить на подтверждение
                </Button>
              </Space>
            </Card>
          )}
      </>
    );
  };

  const renderPoolItemDiscussionActions = (order: any, item: any) => {
    if (!isCustomer) return null;
    const employeeName = item.user?.fullName || item.user?.username || 'Сотрудник';
    const dishName = item.menuItem?.name || 'Блюдо';
    return (
      <Space direction="vertical" size={4} style={{ alignItems: 'flex-end' }}>
        <Button
          size="small"
          icon={<MessageOutlined />}
          onClick={() =>
            openIssueDiscussion(order.id, {
              subjectEmployeeId: item.userId,
              subjectMenuItemId: item.menuItemId,
              subjectLabel: `${employeeName} — ${dishName}`
            })
          }
        >
          Обсудить
        </Button>
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 'auto' }}
          onClick={() =>
            openIssueDiscussion(order.id, {
              subjectEmployeeId: item.userId,
              subjectLabel: employeeName
            })
          }
        >
          По сотруднику
        </Button>
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 'auto' }}
          onClick={() =>
            openIssueDiscussion(order.id, {
              subjectMenuItemId: item.menuItemId,
              subjectLabel: dishName
            })
          }
        >
          По блюду
        </Button>
      </Space>
    );
  };

  return (
    <>
      {isCustomer && renderPoolCard()}
      {isCustomer && renderWeeklyPools()}
      <Card 
        title="Заказы" 
        loading={loading}
        extra={
          <Space wrap>
            {isCustomerManager && (
              <Text type="secondary">Все заказы компании, включая прошлые</Text>
            )}
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ minWidth: 180 }}
              options={[
                { value: 'all', label: 'Все статусы' },
                { value: 'pending', label: 'Ожидает' },
                { value: 'confirmed', label: 'Подтвержден' },
                { value: 'in_preparation', label: 'Готовится' },
                { value: 'in_delivery', label: 'В доставке' },
                { value: 'delivered', label: 'Доставлен' },
                { value: 'cancelled', label: 'Отменен' }
              ]}
            />
            {user?.role === 'employee' && (
              <Button type="primary" onClick={() => message.info('Создание заказа в разработке')}>
                Новый заказ
              </Button>
            )}
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={filteredOrders}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: 'Нет заказов' }}
          scroll={isMobile ? { x: 'max-content' } : undefined}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      <Modal
        title={
          orderDetailModal.order ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
                flexWrap: 'wrap',
                width: '100%',
                paddingRight: 40
              }}
            >
              <Space direction="vertical" size={2}>
                <span style={{ fontSize: 16, fontWeight: 600 }}>Детали заказа</span>
                <Text type="secondary">№ {orderDetailModal.order.id}</Text>
              </Space>
              {renderMenuDayTitleBox(orderDetailModal.order)}
            </div>
          ) : (
            'Детали заказа'
          )
        }
        open={orderDetailModal.visible}
        onCancel={() => setOrderDetailModal({ visible: false, order: null })}
        footer={[
          <Button key="close" onClick={() => setOrderDetailModal({ visible: false, order: null })}>
            Закрыть
          </Button>
        ]}
        width={isMobile ? '100%' : 800}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        {orderDetailModal.order && (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Оформлен в системе" span={2}>
                {new Date(orderDetailModal.order.createdAt).toLocaleString('ru')}
              </Descriptions.Item>
              <Descriptions.Item label="ID заказа">{orderDetailModal.order.id}</Descriptions.Item>
              <Descriptions.Item label="Поставщик">
                {orderDetailModal.order.supplier?.companyName || orderDetailModal.order.supplierId}
              </Descriptions.Item>
              <Descriptions.Item label="Заказчик" span={2}>
                {orderDetailModal.order.customer?.user?.companyName || orderDetailModal.order.customer?.companyName || orderDetailModal.order.customerId}
              </Descriptions.Item>
              <Descriptions.Item label="Адрес доставки" span={2}>
                {formatOrderDeliveryAddress(orderDetailModal.order.deliveryAddress) || 'Не указан'}
              </Descriptions.Item>
              {(() => {
                const rc = getCustomerResponsibleContact(orderDetailModal.order);
                return (
                  <>
                    <Descriptions.Item label="Ответственный (ФИО)">
                      {rc.fullName || '—'}
                      {rc.position ? (
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {rc.position}
                        </Text>
                      ) : null}
                    </Descriptions.Item>
                    <Descriptions.Item label="Телефон">{rc.phone || '—'}</Descriptions.Item>
                  </>
                );
              })()}
              <Descriptions.Item label="Сумма" span={2}>
                <Space direction="vertical" size={0}>
                  <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                    {orderDetailModal.order.totalAmount} ₽
                  </Text>
                  {orderDetailModal.order.originalTotalAmount != null &&
                    orderDetailModal.order.originalTotalAmount !== orderDetailModal.order.totalAmount && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Исходная: {orderDetailModal.order.originalTotalAmount} ₽
                      </Text>
                    )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Статус" span={2}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                  <Space wrap align="center">
                    <Tag color={getStatusColor(orderDetailModal.order.status)}>
                      {getStatusText(orderDetailModal.order.status)}
                    </Tag>
                    {isSupplier && supplierNextStatusOptions(orderDetailModal.order.status).length > 0 && (
                      <Select
                        placeholder="Следующий этап"
                        style={{ minWidth: 220 }}
                        options={supplierNextStatusOptions(orderDetailModal.order.status)}
                        onChange={(v) => handleSupplierSetStatus(orderDetailModal.order.id, v)}
                      />
                    )}
                  </Space>
                  {getCancelNegotiationListLabel(orderDetailModal.order) ? (
                    <Tag color="volcano" style={{ margin: 0 }}>
                      {getCancelNegotiationListLabel(orderDetailModal.order)}
                    </Tag>
                  ) : null}
                </div>
              </Descriptions.Item>
            </Descriptions>

            {orderDetailModal.order.status !== 'delivered' &&
              orderDetailModal.order.status !== 'cancelled' &&
              (isCustomer || isSupplier) && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                  message="Согласование отмены заказа"
                  description={
                    <Space direction="vertical" size="small">
                      <div>
                        Заказчик:{' '}
                        {orderDetailModal.order.cancelCustomerAgreed ? (
                          <Tag color="success">согласен</Tag>
                        ) : (
                          <Tag>ожидается</Tag>
                        )}
                      </div>
                      <div>
                        Поставщик:{' '}
                        {orderDetailModal.order.cancelSupplierAgreed ? (
                          <Tag color="success">согласен</Tag>
                        ) : (
                          <Tag>ожидается</Tag>
                        )}
                      </div>
                      {orderDetailModal.order.cancelReasonCustomer ? (
                        <div>
                          <Text type="secondary">Причина (заказчик): </Text>
                          <Text>{orderDetailModal.order.cancelReasonCustomer}</Text>
                        </div>
                      ) : null}
                      {orderDetailModal.order.cancelReasonSupplier ? (
                        <div>
                          <Text type="secondary">Причина (поставщик): </Text>
                          <Text>{orderDetailModal.order.cancelReasonSupplier}</Text>
                        </div>
                      ) : null}
                      {isCustomer && !orderDetailModal.order.cancelCustomerAgreed && (
                        <Button onClick={() => openCancelAgreeModal(orderDetailModal.order.id)}>
                          Подтвердить согласие на отмену
                        </Button>
                      )}
                      {isSupplier && !orderDetailModal.order.cancelSupplierAgreed && (
                        <Button onClick={() => openCancelAgreeModal(orderDetailModal.order.id)}>
                          Подтвердить согласие на отмену
                        </Button>
                      )}
                    </Space>
                  }
                />
              )}

            {orderDetailModal.order.status === 'cancelled' &&
              (orderDetailModal.order.cancelReasonCustomer ||
                orderDetailModal.order.cancelReasonSupplier ||
                orderDetailModal.order.cancelReasonAdmin) && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                  message="Причины отмены"
                  description={
                    <Space direction="vertical" size="small">
                      {orderDetailModal.order.cancelReasonCustomer ? (
                        <div>
                          <Text type="secondary">Заказчик: </Text>
                          <Text>{orderDetailModal.order.cancelReasonCustomer}</Text>
                        </div>
                      ) : null}
                      {orderDetailModal.order.cancelReasonSupplier ? (
                        <div>
                          <Text type="secondary">Поставщик: </Text>
                          <Text>{orderDetailModal.order.cancelReasonSupplier}</Text>
                        </div>
                      ) : null}
                      {orderDetailModal.order.cancelReasonAdmin ? (
                        <div>
                          <Text type="secondary">Администратор: </Text>
                          <Text>{orderDetailModal.order.cancelReasonAdmin}</Text>
                        </div>
                      ) : null}
                    </Space>
                  }
                />
              )}

            {isCustomer && orderDetailModal.order.status === 'in_delivery' && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 16 }}
                message="Получение заказа при передаче"
                description={
                  orderDetailModal.order.receiptConfirmedAt ? (
                    <Text>
                      Подтверждено:{' '}
                      {new Date(orderDetailModal.order.receiptConfirmedAt).toLocaleString('ru')}
                    </Text>
                  ) : (
                    <Button type="primary" onClick={() => handleConfirmReceipt(orderDetailModal.order.id)}>
                      Подтвердить получение заказа
                    </Button>
                  )
                }
              />
            )}

            {isDelivery && orderDetailModal.order.status === 'in_delivery' && (
              <Alert
                style={{ marginTop: 16 }}
                type={orderDetailModal.order.receiptConfirmedAt ? 'success' : 'warning'}
                showIcon
                message={
                  orderDetailModal.order.receiptConfirmedAt
                    ? 'Заказчик подтвердил получение — отметьте заказ доставленным.'
                    : 'Ожидайте, пока заказчик подтвердит получение заказа.'
                }
                action={
                  orderDetailModal.order.receiptConfirmedAt ? (
                    <Popconfirm
                      title="Отметить заказ доставленным?"
                      okText="Да"
                      cancelText="Нет"
                      onConfirm={() => handleMarkDelivered(orderDetailModal.order.id)}
                    >
                      <Button type="primary">Доставлен</Button>
                    </Popconfirm>
                  ) : undefined
                }
              />
            )}

            {isSupplier && orderDetailModal.order.status === 'in_delivery' && (
              <Alert
                style={{ marginTop: 16 }}
                type="info"
                showIcon
                message="Отметка доставки (если доставщик внешний)"
                action={
                  <Popconfirm
                    title="Отметить заказ доставленным?"
                    okText="Да"
                    cancelText="Нет"
                    onConfirm={() => handleMarkDelivered(orderDetailModal.order.id)}
                  >
                    <Button type="primary">Доставлен</Button>
                  </Popconfirm>
                }
              />
            )}

            {isEmployee && orderDetailModal.order.status === 'delivered' && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 16 }}
                message="Несоответствие при получении?"
                description={
                  <Space direction="vertical" size="small">
                    <Text type="secondary">
                      Можно открыть форму обращения к поставщику с фото и описанием — заказчик увидит переписку.
                    </Text>
                    <Button
                      type="primary"
                      onClick={() => {
                        navigate(`/order-issues?orderId=${orderDetailModal.order.id}`);
                        setOrderDetailModal({ visible: false, order: null });
                      }}
                    >
                      Сообщить о проблеме
                    </Button>
                  </Space>
                }
              />
            )}

            {renderOrderCorrectionsBlock(orderDetailModal.order)}

            <Divider>Позиции заказа (итоговые)</Divider>
            <List
              dataSource={orderDetailModal.order.items}
              renderItem={(item: any) => (
                <List.Item>
                  <List.Item.Meta
                    title={item.menuItem?.name || 'Блюдо'}
                    description={`Цена: ${item.price} ₽ × ${item.quantity} = ${(item.price * item.quantity).toFixed(2)} ₽`}
                  />
                </List.Item>
              )}
            />

            {orderDetailModal.order.comboItems && orderDetailModal.order.comboItems.length > 0 && (
              <>
                <Divider>Комбо (итоговые)</Divider>
                <List
                  dataSource={orderDetailModal.order.comboItems}
                  renderItem={(ci: any) => {
                    const groups = Array.isArray(ci.selectionsSnapshot) ? ci.selectionsSnapshot : [];
                    return (
                      <List.Item>
                        <List.Item.Meta
                          title={`${ci.titleSnapshot || ci.combo?.title || 'Комбо'} × ${ci.quantity}`}
                          description={
                            <Space direction="vertical" size={4}>
                              <Text type="secondary">
                                Цена: {ci.unitPrice} ₽ × {ci.quantity} = {(ci.priceTotal ?? ci.unitPrice * ci.quantity).toFixed(2)} ₽
                              </Text>
                              {groups.length > 0 ? (
                                <div>
                                  {groups.map((g: any) => (
                                    <div key={g.groupId || g.title}>
                                      <Text>
                                        {g.title}: {(g.items || []).map((it: any) => it.name).join(', ') || '—'}
                                      </Text>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </Space>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </>
            )}

            {orderDetailModal.order.orderPool && orderDetailModal.order.orderPool.items && orderDetailModal.order.orderPool.items.length > 0 && (
              <>
                <Divider>Детализация по сотрудникам</Divider>
                <List
                  dataSource={orderDetailModal.order.orderPool.items}
                  renderItem={(item: any) => (
                    <List.Item actions={[renderPoolItemDiscussionActions(orderDetailModal.order, item)]}>
                      <List.Item.Meta
                        title={`${item.menuItem?.name || 'Блюдо'} — ${item.quantity} шт`}
                        description={
                          <>
                            <div>Сотрудник: {item.user?.fullName || item.user?.username || 'Сотрудник'}</div>
                            {item.employeeNote ? (
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary">Комментарий: {item.employeeNote}</Text>
                              </div>
                            ) : null}
                          </>
                        }
                      />
                      <Text>{(item.menuItem?.price || 0) * item.quantity} ₽</Text>
                    </List.Item>
                  )}
                />
              </>
            )}

            {orderDetailModal.order.orderPool &&
              orderDetailModal.order.orderPool.comboItems &&
              orderDetailModal.order.orderPool.comboItems.length > 0 && (
                <>
                  <Divider>Комбо по сотрудникам</Divider>
                  <List
                    dataSource={orderDetailModal.order.orderPool.comboItems}
                    renderItem={(ci: any) => {
                      const groups = Array.isArray(ci.selectionsSnapshot) ? ci.selectionsSnapshot : [];
                      return (
                        <List.Item>
                          <List.Item.Meta
                            title={`${ci.titleSnapshot || ci.combo?.title || 'Комбо'} — ${ci.quantity} шт`}
                            description={
                              <Space direction="vertical" size={4}>
                                <div>Сотрудник: {ci.user?.fullName || ci.user?.username || 'Сотрудник'}</div>
                                {groups.length > 0 ? (
                                  <div>
                                    {groups.map((g: any) => (
                                      <div key={g.groupId || g.title}>
                                        <Text type="secondary">
                                          {g.title}: {(g.items || []).map((it: any) => it.name).join(', ') || '—'}
                                        </Text>
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </Space>
                            }
                          />
                          <Text>{ci.priceTotal ?? (ci.unitPrice || 0) * (ci.quantity || 1)} ₽</Text>
                        </List.Item>
                      );
                    }}
                  />
                </>
              )}

            {orderDetailModal.order.employeeOrderRecords && orderDetailModal.order.employeeOrderRecords.length > 0 && (
              <>
                <Divider>Суммы по сотрудникам</Divider>
                <List
                  dataSource={orderDetailModal.order.employeeOrderRecords}
                  renderItem={(record: any) => (
                    <List.Item>
                      <List.Item.Meta title={record.employee?.fullName || record.employee?.username || 'Сотрудник'} />
                      <Text strong>{record.amount} ₽</Text>
                    </List.Item>
                  )}
                />
              </>
            )}
          </>
        )}
      </Modal>

      {/* Модальное окно чек-листа */}
      <Modal
        title={
          <Space>
            <CheckSquareOutlined />
            <span>Чек-лист заказа #{checklistModal.order?.id}</span>
            {checklistModal.order && (
              <Switch
                checked={checklistModal.order.checklistVerified}
                checkedChildren="Проверен"
                unCheckedChildren="Не проверен"
                onChange={(checked) => handleToggleChecklistVerified(checklistModal.order.id, checked)}
                style={{ marginLeft: 16 }}
              />
            )}
          </Space>
        }
        open={checklistModal.visible}
        onCancel={() => setChecklistModal({ visible: false, order: null })}
        footer={[
          <Button key="print" icon={<PrinterOutlined />} onClick={() => {
            const printContent = document.getElementById('checklist-content');
            if (printContent) {
              const printWindow = window.open('', '_blank');
              if (printWindow) {
                printWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                    <head>
                      <title>Чек-лист заказа #${checklistModal.order?.id}</title>
                      <style>
                        @media print {
                          body { margin: 0; }
                          @page { margin: 15mm; }
                          .no-print { display: none !important; }
                        }
                        body {
                          font-family: Arial, sans-serif;
                          padding: 20px;
                        }
                        table {
                          width: 100%;
                          border-collapse: collapse;
                          margin: 20px 0;
                        }
                        th, td {
                          border: 1px solid #ddd;
                          padding: 8px;
                          text-align: left;
                        }
                        th {
                          background-color: #f2f2f2;
                          font-weight: bold;
                        }
                        .header {
                          text-align: center;
                          border-bottom: 2px solid #1890ff;
                          padding-bottom: 16px;
                          margin-bottom: 24px;
                        }
                        .info-card {
                          border: 1px solid #ddd;
                          padding: 12px;
                          margin: 12px 0;
                        }
                        .total {
                          font-size: 18px;
                          font-weight: bold;
                          color: #1890ff;
                        }
                      </style>
                    </head>
                    <body>
                      ${printContent.innerHTML}
                    </body>
                  </html>
                `);
                printWindow.document.close();
                setTimeout(() => {
                  printWindow.print();
                }, 500);
              }
            }
          }}>
            Печать заказа
          </Button>,
          <Button key="close" onClick={() => setChecklistModal({ visible: false, order: null })}>
            Закрыть
          </Button>
        ]}
        width={isMobile ? '100%' : 1200}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        {checklistModal.order && (
          <div id="checklist-content" style={{ padding: '20px 0' }}>
            {/* Заголовок отчета */}
            <div style={{ textAlign: 'center', marginBottom: 24, borderBottom: '2px solid #1890ff', paddingBottom: 16 }}>
              <Typography.Title level={3} style={{ margin: 0 }}>
                Чек-лист заказа #{checklistModal.order.id}
              </Typography.Title>
              <Space style={{ marginTop: 8 }}>
                <Text>Дата создания: {new Date(checklistModal.order.createdAt).toLocaleString('ru')}</Text>
                <Divider type="vertical" />
                <Text>
                  День доставки (сотрудники выбрали блюда на):{' '}
                  {checklistModal.order.deliveryDate
                    ? new Date(checklistModal.order.deliveryDate).toLocaleDateString('ru')
                    : 'Не указан'}
                </Text>
              </Space>
            </div>

            {/* Информация о заказчике и поставщике */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card size="small" title="Заказчик">
                  <Text strong>{checklistModal.order.customer?.user?.companyName || checklistModal.order.customer?.companyName || `ID: ${checklistModal.order.customerId}`}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      Адрес: {formatOrderDeliveryAddress(checklistModal.order.deliveryAddress) || 'Не указан'}
                    </Text>
                  </div>
                  {(() => {
                    const rc = getCustomerResponsibleContact(checklistModal.order);
                    return (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">
                          Ответственный: {rc.fullName || '—'}
                          {rc.position ? ` (${rc.position})` : ''}
                        </Text>
                        <br />
                        <Text type="secondary">Тел.: {rc.phone || '—'}</Text>
                      </div>
                    );
                  })()}
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Поставщик">
                  <Text strong>{checklistModal.order.supplier?.companyName || `ID: ${checklistModal.order.supplierId}`}</Text>
                </Card>
              </Col>
            </Row>

            {/* Таблица с блюдами */}
            <Table
              dataSource={checklistModal.order.items || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: '№',
                  key: 'index',
                  width: 50,
                  render: (_: any, __: any, index: number) => index + 1
                },
                {
                  title: 'Блюдо',
                  dataIndex: ['menuItem', 'name'],
                  key: 'name',
                  render: (name: string, record: any) => (
                    <Space>
                      <Text strong>{name || 'Блюдо'}</Text>
                      {record.menuItem?.imageUrl && (
                        <img 
                          src={record.menuItem.imageUrl} 
                          alt={name}
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                        />
                      )}
                    </Space>
                  )
                },
                {
                  title: 'Количество',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  width: 100,
                  align: 'center',
                  render: (qty: number) => <Text strong>{qty} шт</Text>
                },
                {
                  title: 'Цена за единицу',
                  dataIndex: 'price',
                  key: 'price',
                  width: 120,
                  align: 'right',
                  render: (price: number) => `${price.toFixed(2)} ₽`
                },
                {
                  title: 'Сумма',
                  key: 'total',
                  width: 120,
                  align: 'right',
                  render: (_: any, record: any) => (
                    <Text strong style={{ fontSize: 14 }}>
                      {(record.price * record.quantity).toFixed(2)} ₽
                    </Text>
                  )
                }
              ]}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4} align="right">
                      <Text strong style={{ fontSize: 16 }}>Итого:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                        {checklistModal.order.totalAmount?.toFixed(2) || '0.00'} ₽
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            {checklistModal.order.comboItems && checklistModal.order.comboItems.length > 0 && (
              <>
                <Divider>Комбо</Divider>
                <Table
                  dataSource={checklistModal.order.comboItems}
                  rowKey={(r: any) => `combo-${r.id}`}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '№',
                      key: 'index',
                      width: 50,
                      render: (_: any, __: any, index: number) => index + 1
                    },
                    {
                      title: 'Комбо',
                      key: 'combo',
                      render: (_: any, record: any) => (
                        <Space direction="vertical" size={2}>
                          <Text strong>{record.titleSnapshot || record.combo?.title || 'Комбо'}</Text>
                          {Array.isArray(record.selectionsSnapshot) && record.selectionsSnapshot.length > 0 ? (
                            <Text type="secondary">
                              {record.selectionsSnapshot
                                .map((g: any) => `${g.title}: ${(g.items || []).map((it: any) => it.name).join(', ')}`)
                                .join(' | ')}
                            </Text>
                          ) : null}
                        </Space>
                      )
                    },
                    {
                      title: 'Количество',
                      dataIndex: 'quantity',
                      key: 'quantity',
                      width: 100,
                      align: 'center',
                      render: (qty: number) => <Text strong>{qty} шт</Text>
                    },
                    {
                      title: 'Цена за единицу',
                      dataIndex: 'unitPrice',
                      key: 'unitPrice',
                      width: 120,
                      align: 'right',
                      render: (price: number) => `${(price ?? 0).toFixed(2)} ₽`
                    },
                    {
                      title: 'Сумма',
                      key: 'total',
                      width: 120,
                      align: 'right',
                      render: (_: any, record: any) => (
                        <Text strong style={{ fontSize: 14 }}>
                          {(record.priceTotal ?? (record.unitPrice || 0) * (record.quantity || 1)).toFixed(2)} ₽
                        </Text>
                      )
                    }
                  ]}
                />
              </>
            )}

            {/* QR-коды для печати по сотрудникам, если есть */}
            {checklistModal.order.orderPool && checklistModal.order.orderPool.items && checklistModal.order.orderPool.items.length > 0 && (
              <>
                <Divider>QR-коды для печати</Divider>
                <Table
                  dataSource={checklistModal.order.orderPool.items}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '№',
                      key: 'index',
                      width: 50,
                      render: (_: any, __: any, index: number) => index + 1
                    },
                    {
                      title: 'Сотрудник',
                      key: 'employee',
                      render: (_: any, record: any) => (
                        <Text strong>{formatEmployeeName(record.user)}</Text>
                      )
                    },
                    {
                      title: 'Блюдо',
                      dataIndex: ['menuItem', 'name'],
                      key: 'dish',
                      render: (name: string) => <Text strong>{name}</Text>
                    },
                    {
                      title: 'Комментарий',
                      key: 'note',
                      ellipsis: true,
                      render: (_: any, r: any) =>
                        r.employeeNote ? <Text type="secondary">{r.employeeNote}</Text> : '—'
                    },
                    {
                      title: 'Количество',
                      dataIndex: 'quantity',
                      key: 'quantity',
                      align: 'center',
                      render: (qty: number) => <Text strong>{qty} шт</Text>
                    },
                    {
                      title: 'Цена',
                      dataIndex: ['menuItem', 'price'],
                      key: 'price',
                      align: 'right',
                      render: (price: number) => `${price?.toFixed(2) || '0.00'} ₽`
                    },
                    {
                      title: 'QR-код',
                      key: 'qr',
                      width: 150,
                      align: 'center',
                      render: (_: any, poolItem: any) => {
                        const employeeName = formatEmployeeName(poolItem.user);
                        const qrData = JSON.stringify({
                          orderId: checklistModal.order.id,
                          poolItemId: poolItem.id,
                          menuItemId: poolItem.menuItemId,
                          menuItemName: poolItem.menuItem?.name,
                          quantity: poolItem.quantity,
                          userId: poolItem.userId,
                          username: employeeName,
                          fullName: employeeName,
                          employeeNote: poolItem.employeeNote || null,
                          customerId: checklistModal.order.customerId,
                          supplierId: checklistModal.order.supplierId,
                          deliveryDate: checklistModal.order.deliveryDate,
                          printDate: new Date().toLocaleDateString('ru')
                        });
                        
                        return (
                          <div style={{ textAlign: 'center' }}>
                            <QRCode 
                              value={qrData}
                              size={80}
                              style={{ marginBottom: 4 }}
                            />
                            <div>
                              <Button 
                                size="small" 
                                icon={<PrinterOutlined />}
                                onClick={() => printQRCode(poolItem, checklistModal.order)}
                                type="primary"
                              >
                                Печать
                              </Button>
                            </div>
                          </div>
                        );
                      }
                    }
                  ]}
                />
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="Согласие на отмену заказа"
        open={cancelAgreeForOrderId !== null}
        okText="Отправить"
        cancelText="Закрыть"
        onOk={submitCancelAgreeModal}
        onCancel={() => {
          setCancelAgreeForOrderId(null);
          setCancelReasonDraft('');
        }}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Опишите причину отмены (не менее {CANCEL_MIN_REASON_LEN} символов, не более{' '}
          {CANCEL_MAX_REASON_LEN}). Без текста отправить нельзя.
        </Text>
        <Input.TextArea
          rows={4}
          maxLength={CANCEL_MAX_REASON_LEN}
          showCount
          value={cancelReasonDraft}
          onChange={(e) => setCancelReasonDraft(e.target.value)}
          placeholder="Например: изменились планы по обеду, нужно перенести дату…"
        />
      </Modal>

      <Modal
        title="Обсуждение с поставщиком"
        open={issueModal.visible}
        onCancel={() => setIssueModal((prev) => ({ ...prev, visible: false, loading: false }))}
        onOk={submitIssueDiscussion}
        okText="Открыть чат"
        confirmLoading={issueModal.loading}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text type="secondary">
            Заказ №{issueModal.orderId}
            {issueModal.subjectLabel ? ` · ${issueModal.subjectLabel}` : ''}
          </Text>
          <Input.TextArea
            rows={4}
            value={issueModal.message}
            onChange={(e) => setIssueModal((prev) => ({ ...prev, message: e.target.value }))}
            placeholder="Опишите вопрос или проблему для поставщика"
            maxLength={2000}
            showCount
          />
        </Space>
      </Modal>
    </>
  );
};

export default Orders;

